import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'

import { CARD_THEME, Card } from '@st-operations/uikit/src/v2'
import toast from '@st-operations/uikit/lib/toast'

import ListView from '../list-view'
import { getCategoryOverindex } from '_services/data-explorer'

const OverindexingCategoriesSelector = ({ className, audience, onSelectionChange }) => {
  const [isContextualPro, setIsContextualPro] = useState(false)

  const [error, setError] = useState()
  const [data, setData] = useState()
  const [loading, setLoading] = useState(false)

  const selection = useMemo(() => audience.contextualProCategories?.map((c) => c.value), [audience])

  const hasDataRef = useRef(false)

  // auto-select top 5 categories on first result if audience categories is empty
  useEffect(() => {
    if (data && !hasDataRef.current && !selection?.length) {
      hasDataRef.current = true
      const topFive = data.slice(0, 5)
      onSelectionChange(topFive)
      toast('Contextual Pro categories selected')
    }
  }, [data])

  useEffect(() => {
    const hasContextualProDestination = audience?.destinations?.some(
      (d) => d.slug?.startsWith('contextual-plus') || d.slug?.startsWith('contextual-pro')
    )
    setIsContextualPro(hasContextualProDestination)
    if (!hasContextualProDestination) {
      //no need to proceed... it's not a contextual pro audience
      return
    }

    if (!audience.topics?.length > 0 && !audience.regions?.length > 0) {
      setError('add-topics-and-regions')
      return
    }
    if (!audience.topics?.length > 0) {
      setError('add-topics')
      return
    }
    if (!audience.regions?.length > 0) {
      setError('add-regions')
      return
    }

    async function fetch() {
      try {
        setLoading(true)

        const countries = audience?.regions
          .map((region) => region.countries.map((country) => ({ code: country })))
          .filter(({ code }) => code && code !== 'GLOBAL')
          .flat()
        const geoIsoFilter = {
          key: 'geo_iso',
          operator: 'in',
          value: countries,
        }
        const filters = [
          {
            key: 'keywords',
            operator: 'in',
            value: audience.topics
              .map((topic) => topic.atoms?.keywords)
              .filter(Boolean)
              .flat(),
          },
          {
            key: 'source',
            operator: 'in',
            value: ['sharethis'],
          },
        ]
        if (countries.length > 0) {
          filters.push(geoIsoFilter)
        }
        const response = await getCategoryOverindex({
          filters,
          start: formatDate(ONE_YEAR_AGO),
          end: formatDate(TWO_DAYS_AGO),
        })
        const sample = response.L3

        const objectsSample = Object.keys(sample)
          .map((key) => ({ _id: key, value: key, weight: sample[key] /*?.overindex*/ }))
          .sort((a, b) => b.weight - a.weight)
        setData(objectsSample)
        setError() //no error
        setLoading(false)
      } catch (error) {
        console.error(error)
        setError(error.message)
        toast.error(error.message)
      }
    }
    fetch()
  }, [audience?.topics, audience?.regions])

  const handleSelectionChange = (param) => {
    const { currentSelected } = param
    const selectedItems = data.filter((f) => currentSelected.includes(f._id))
    onSelectionChange(selectedItems)
  }

  return (
    <If condition={isContextualPro}>
      <Card theme={CARD_THEME.NO_SHADOW} className={classNames('topics-card__card', className)}>
        <header className="topics-card__header min-h-[56px]">
          <div>
            <span className="text-gray-700 font-semibold text-[14px]">
              Configure Contextual Pro:
            </span>
          </div>
        </header>
        <div className="flex flex-wrap w-full p-5 bg-[#f8f8f8] ">
          <Choose>
            <When condition={loading}>Loading data...</When>
            <When condition={error === 'add-topics-and-regions'}>
              You need to add regions and topics to the audience in order to see this data
            </When>
            <When condition={error === 'add-topics'}>
              You need to add topics to the audience in order to see this data
            </When>
            <When condition={error === 'add-regions'}>
              You need to add regions to the audience in order to see this data
            </When>
            <Otherwise>
              <ListView
                key={loading}
                className="bg-transparent"
                showCheckboxes
                searchable={false}
                columns={[
                  {
                    name: 'Category',
                    key: 'value',
                    width: '400px',
                  },
                  {
                    name: 'Index',
                    key: 'weight',
                    width: '100px',
                    styles: {
                      flexGrow: 0,
                    },
                  },
                ]}
                data={data}
                selection={selection}
                onSelectionChange={handleSelectionChange}
                page_size={10}
              />
            </Otherwise>
          </Choose>
        </div>
      </Card>
    </If>
  )
}

const TWO_DAYS_AGO = dayjs().subtract(2, 'day')
const ONE_YEAR_AGO = dayjs().subtract(1, 'year')
const formatDate = (_dayjs) => _dayjs.format('MM/DD/YYYY') // format accepted by backend

export default OverindexingCategoriesSelector
